import getTemplateDownloadsCount from "utils/template-utils";
import { CanvasTemplateMinimal } from "shared/datamodel/schemas";
import StyledButton, { ButtonSize, ButtonStyle } from "frontend/ui-components/styled-button";
import { CSSProperties, useRef, useState } from "react";
import style from "./template-card-v2.module.css";
import { Crown } from "frontend/icons/crown";
import { ContextMenuOption, OptionsContextMenu } from "frontend/context-menu/optionsContextMenu";
import { getPathPrefix } from "utils/getPathPrefix";
import { PortalID } from "frontend/modal/portal-types";
import { MenuHorizontalIcon } from "frontend/icons/menu-horizontal";
import { Tooltip } from "frontend/ui-components/design-system/tooltip/tooltip";
import DownloadIcon from "frontend/icons/download";

interface TemplateCardV2Props {
  template: CanvasTemplateMinimal;
  previewAvailable: boolean;
  onClick: (template: CanvasTemplateMinimal) => void;
  onPreview: (template: CanvasTemplateMinimal) => void;
  onEditTemplate: (template: CanvasTemplateMinimal) => void;
  onDeleteTemplate: (template: CanvasTemplateMinimal) => void;
  cssVariables?: CSSProperties;
}

export default function TemplateCardV2({
  template,
  previewAvailable,
  onClick,
  onPreview,
  onEditTemplate,
  onDeleteTemplate,
}: TemplateCardV2Props) {
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const [showMenu, setShowMenu] = useState(false);

  const templateDownloadsCount = getTemplateDownloadsCount(template.downloadsCount);
  const thumbnailImageURL = template.thumbnailImageURL;
  const shouldShowMenu = !!template.isOwner;

  const dismissMenu = () => setShowMenu(false);
  const onEdit = () => {
    dismissMenu();
    onEditTemplate(template);
  };
  const onDelete = () => {
    dismissMenu();
    onDeleteTemplate(template);
  };

  return (
    <div
      className={style["main-container"]}
      data-testid={template.upgradeRequired ? `upgrade-required-${template.name}` : template.name}
    >
      <TemplateCard
        image={thumbnailImageURL}
        title={template.name}
        number={templateDownloadsCount}
        onClick={() => onClick(template)}
        previewAvailable={previewAvailable}
        onPreview={() => onPreview(template)}
      />
      {template.upgradeRequired && (
        <div className={style["upgradeRequiredContainer"]}>
          <Crown size={10} />
        </div>
      )}
      {shouldShowMenu && (
        <button
          ref={menuButtonRef}
          className={style["options-button"]}
          onClick={() => setShowMenu((p) => !p)}
          data-testid="template_menu"
        >
          <MenuHorizontalIcon />
        </button>
      )}
      {showMenu && (
        <TemplateCardMenu onDismiss={dismissMenu} onEdit={onEdit} onDelete={onDelete} menuButtonRef={menuButtonRef} />
      )}
    </div>
  );
}

interface TemplateCardProps {
  image?: string | null;
  title: string;
  number: string | null;
  onClick: () => void;
  onPreview: () => void;
  previewAvailable: boolean;
}
function TemplateCard({ image, title, number, onClick, previewAvailable, onPreview }: TemplateCardProps) {
  return (
    <div className={style["card"]} onClick={onClick}>
      <div className={style["image-container"]}>
        <img src={image || undefined} alt={title} data-testid={`${title}-template-image-card`} />
        {previewAvailable && (
          <div className={style["overlay"]}>
            <StyledButton
              title="Use Template"
              size={ButtonSize.small}
              style={ButtonStyle.filled}
              customStyle={{ width: 120, height: 26, fontWeight: 400, borderRadius: 6 }}
            />
            <span
              className={style.previewButton}
              onClick={(e) => {
                e.stopPropagation();
                onPreview();
              }}
            >
              Preview
            </span>
          </div>
        )}
      </div>
      <div className={style["title-line"]}>
        <Tooltip text={title} offset={8}>
          <span className={style["title-text"]}>{title}</span>
        </Tooltip>

        <span className={style["title-number"]}>
          <DownloadIcon size={10} /> {number}
        </span>
      </div>
    </div>
  );
}

interface TemplateCardMenuProps {
  onDismiss: () => void;
  onEdit: () => void;
  onDelete: () => void;
  menuButtonRef: React.RefObject<HTMLButtonElement>;
}

function TemplateCardMenu({ menuButtonRef, onDismiss, onEdit, onDelete }: TemplateCardMenuProps) {
  const options: ContextMenuOption[] = [
    {
      title: "Edit template",
      iconPath: getPathPrefix("/images/pencil-icon-new.svg"),
      id: "edit-template",
      onClick: onEdit,
    },
    {
      title: "Delete template",
      iconPath: getPathPrefix("/images/trash-icon.svg"),
      id: "delete-template",
      onClick: onDelete,
    },
  ];
  return (
    <OptionsContextMenu options={options} attachToRef={menuButtonRef} onDismiss={onDismiss} portalID={PortalID.Modal} />
  );
}
